import { Button, Code, Flex, Loader, MultiSelect, ScrollArea, Stack, Tabs, Text, Title } from '@mantine/core';
import { ApiReferenceReact } from '@scalar/api-reference-react';
import '@scalar/api-reference-react/style.css';
import { ReactRenderer } from '@scalar/react-renderer';
import type { ApiReferencePlugin } from '@scalar/types/api-reference';
import { IconDownload } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useJsonResource } from '../../hooks/use-json-resource';
import { useCalcIntercept } from '../../hooks/useCalcIntercept/hook';
import { useDarkMode } from '../../hooks/useDarkMode';
import { trackEvent } from '../../util/mixpanel';
import { getServerUrl } from './code-snippets';
import { CopyIconButton } from './ExplorerParts';
import { buildModuleIndex, compareModules, filterSpecByModules } from './module-filter';
import { getModuleLabel, isModuleId, type ModuleId } from './module-labels';
import ModuleExplorer from './ModuleExplorer';
import OutputExplorer from './OutputExplorer';
import './ScalarReference.css';

const UnitsBadge = ({ xUnits }: { xUnits: unknown }) => (
  <div className="property-name units-badge">
    <span>Units: {String(xUnits)}</span>
  </div>
);

const UnitsPlugin = (): ApiReferencePlugin => {
  return () => {
    return {
      name: 'units-plugin',
      extensions: [
        {
          name: 'x-units',
          component: UnitsBadge,
          renderer: ReactRenderer,
        },
      ],
    };
  };
};

const ModulesBadge = ({ xModules }: { xModules: unknown }) => {
  const modules = (Array.isArray(xModules) ? xModules : [xModules])
    .filter((m) => m !== undefined && m !== null)
    .map(String)
    .sort(compareModules);

  if (modules.length === 0) {
    return null;
  }

  return (
    <div className="property-name modules-badge">
      <span>Modules: {modules.join(', ')}</span>
    </div>
  );
};

const ModulesPlugin = (): ApiReferencePlugin => {
  return () => {
    return {
      name: 'modules-plugin',
      extensions: [
        {
          name: 'x-modules',
          component: ModulesBadge,
          renderer: ReactRenderer,
        },
      ],
    };
  };
};

const plugins = [UnitsPlugin(), ModulesPlugin()];

// The Scalar docs tab is only useful during local development, hide it in deployed environments
// eslint-disable-next-line turbo/no-undeclared-env-vars -- DEV is set by Vite from the build mode, not the environment
const showDocsTab = import.meta.env.DEV;

function ScalarReference({ calculator, version }: { calculator: string; version: string | undefined }) {
  const isDarkMode = useDarkMode();
  useCalcIntercept(version);

  const { loading, error, data: spec } = useJsonResource<Record<string, unknown>>(version, `${calculator}.json`);
  const [selectedModules, setSelectedModules] = useState<ModuleId[]>([]);

  // useJsonResource fetches from the same path, so this downloads the full, unfiltered spec
  const specUrl = `/api/${version}/${calculator}.json`;
  const serverUrl = spec ? getServerUrl(spec) : '';

  // Walk the full document once per spec, so changing the selection only needs the cheap filter pass
  const moduleIndex = useMemo(() => (spec ? buildModuleIndex(spec) : null), [spec]);

  const moduleOptions = useMemo(
    () => moduleIndex?.modules.map((module) => ({ value: module, label: getModuleLabel(module) })) ?? [],
    [moduleIndex],
  );

  const filteredSpec = useMemo(
    () => (spec && moduleIndex ? filterSpecByModules(spec, moduleIndex, new Set(selectedModules)) : null),
    [spec, moduleIndex, selectedModules],
  );

  // Scalar re-processes the document whenever the configuration identity changes, so keep it stable
  const configuration = useMemo(
    () => ({
      content: filteredSpec ?? undefined,
      forceDarkModeState: isDarkMode ? ('dark' as const) : ('light' as const),
      onBeforeRequest({ request }: { request: Request }) {
        trackEvent('api_docs_request', {
          path: request.url,
          method: request.method,
        });
      },
      expandAllModelSections: true,
      hideModels: true,
      plugins,
    }),
    [filteredSpec, isDarkMode],
  );

  return (
    <Flex h="calc(100vh - 120px - 32px)">
      <ScrollArea scrollbars="y" flex={1} miw={0}>
        <div className="api-docs container">
          <Title order={1} mb="md">
            REST API Reference
          </Title>
          <Flex justify="space-between" gap="xl" mb="md">
            <Stack>
              <Text flex={1} miw={300}>
                {/* <Paper withBorder p="md" mb="md"> */}
                Interactive documentation for the Emissions Calculator API. You can view the details of the parameters
                you need to supply to the <Code>{calculator}</Code> calculator, and try out requests directly from this
                page.
                {/* </Paper> */}
              </Text>
              <Flex align="center" gap={4}>
                <Text size="sm" c="dimmed">
                  Server:
                </Text>
                <Code>{serverUrl}</Code>
                <CopyIconButton value={serverUrl} label="Copy server URL" />
              </Flex>
            </Stack>
            <Stack align="flex-end" gap="xs">
              {/* <Text>
                Currently viewing version:{' '}
                <Text span fw={600}>
                  {version}
                </Text>
              </Text> */}

              {spec && (
                <>
                  <Button
                    component="a"
                    href={specUrl}
                    download={`${calculator}-${version}.json`}
                    variant="light"
                    size="xs"
                    leftSection={<IconDownload size={16} />}
                  >
                    Download OpenAPI spec
                  </Button>
                </>
              )}
            </Stack>
          </Flex>

          <div className="api-content">
            {loading ? (
              <Flex justify="center" align="center" w="100%" h="300px">
                <Loader size="lg" />
                <Text ml="md">Loading API specification...</Text>
              </Flex>
            ) : error ? (
              <Flex direction="column" align="center" w="100%" h="300px" justify="center">
                <Text c="red" size="lg" mb="md">
                  {error}
                </Text>
                <Text size="sm" c="dimmed">
                  Please try refreshing the page or contact support if the issue persists.
                </Text>
              </Flex>
            ) : spec && moduleIndex && filteredSpec ? (
              <Tabs defaultValue="inputs">
                <Tabs.List mb="md">
                  {showDocsTab && <Tabs.Tab value="docs">Docs</Tabs.Tab>}
                  <Tabs.Tab value="inputs">Inputs</Tabs.Tab>
                  <Tabs.Tab value="outputs">Outputs</Tabs.Tab>
                </Tabs.List>

                {showDocsTab && (
                  <Tabs.Panel value="docs">
                    <Title order={2}>API endpoints</Title>
                    <br />
                    {moduleOptions.length > 0 && (
                      <MultiSelect
                        label="Filter by module"
                        description="Only show the input fields used by the selected modules"
                        placeholder={selectedModules.length === 0 ? 'All modules' : undefined}
                        data={moduleOptions}
                        value={selectedModules}
                        onChange={(value) => setSelectedModules(value.filter(isModuleId).sort(compareModules))}
                        searchable
                        clearable
                        mb="md"
                      />
                    )}
                    <div className="swagger-container">
                      <ApiReferenceReact configuration={configuration} />
                    </div>
                  </Tabs.Panel>
                )}

                <Tabs.Panel value="inputs">
                  <ModuleExplorer
                    calculator={calculator}
                    version={version}
                    spec={spec}
                    moduleIndex={moduleIndex}
                    selectedModules={selectedModules}
                    onSelectedModulesChange={setSelectedModules}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="outputs">
                  <OutputExplorer spec={spec} moduleIndex={moduleIndex} />
                </Tabs.Panel>
              </Tabs>
            ) : null}
          </div>
        </div>
      </ScrollArea>
    </Flex>
  );
}

export default ScalarReference;
